// App.tsx — root router. Two pages, exactly as the product is scoped: rate,
// and reveal. The shared Shell from lib/web supplies the navbar, the
// Darkly/Flatly toggle and the full-bleed page container.
//
// There is no upload route, no corpus browser and no leaderboard. The corpus
// is administered through /api/admin, which this app never calls, and no
// endpoint exists that would rank people for an audience.
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Spinner from 'react-bootstrap/Spinner'
import { BsSliders } from 'react-icons/bs'
import { Shell, OidcLoginPage, useAuth } from 'common'
import type { ShellNavItem } from 'common'
import { apiClient } from './api'
import RatePage from './pages/RatePage'
import RevealPage from './pages/RevealPage'

const navItems: ShellNavItem[] = [
    { label: 'Rate', href: '/' },
    { label: 'Reveal', href: '/reveal' },
]

function Root() {
    const auth = useAuth({ client: apiClient })

    // Hold the routes back until /api/me has answered. Rendering them during
    // the probe mounts RatePage, which immediately asks for a pair and takes a
    // 401 the instant before the app redirects to login — a wasted round-trip
    // and a flash of an error the user cannot act on.
    if (auth.loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Signing in…</span>
                </Spinner>
            </div>
        )
    }

    // Every page here is per-rater, so there is nothing to show a signed-out
    // visitor: send them straight into the OIDC round-trip rather than
    // rendering an empty shell they cannot use.
    if (!auth.authenticated) {
        return (
            <Routes>
                <Route path="*" element={<OidcLoginPage returnTo="/" />} />
            </Routes>
        )
    }

    return (
        <Shell
            brand={<><BsSliders size={18} /> Objectifio</>}
            navItems={navItems}
            userEmail={auth.authenticated ? auth.email : undefined}
            userPhotoUrl={auth.photoUrl || undefined}
            onLogout={auth.authenticated
                ? () => { window.location.href = '/api/oidc/logout' }
                : undefined}
        >
            <Routes>
                <Route path="/" element={<RatePage />} />
                <Route path="/reveal" element={<RevealPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Shell>
    )
}

function App() {
    return (
        <BrowserRouter>
            <Root />
        </BrowserRouter>
    )
}

export default App
